/**
 * Sim → presentation event queue. The simulation never touches three.js or the
 * DOM; it pushes plain events here and the render/UI layers drain them after
 * each tick. Events are pooled-free small objects; volume is low (kills, hits,
 * level-ups), so allocation is acceptable outside the per-projectile hot path.
 */
export type SimEvent =
  | { type: 'hit'; x: number; y: number; dmg: number; crit: boolean; side: 0 | 1; projectile?: ProjectileEventVisual }
  | { type: 'kill'; x: number; y: number; enemyDef: string; elite: boolean }
  | { type: 'playerHit'; player: number; dmg: number; projectile?: ProjectileEventVisual }
  | { type: 'playerDeath'; player: number }
  | { type: 'levelUp'; player: number; level: number }
  | { type: 'pickup'; player: number; kind: 'xp' | 'heal' | 'shard'; amount: number }
  | { type: 'dash'; player: number; x: number; y: number; dx: number; dy: number }
  | { type: 'ability'; player: number; ability: 'warCry' | 'arcaneNova' | 'piercingVolley' | 'phaseStep'; x: number; y: number; dx: number; dy: number }
  | { type: 'bossSpawn'; defId: string }
  | { type: 'bossPhase'; phase: number }
  | { type: 'bossDeath'; defId: string; x: number; y: number }
  | { type: 'enemyFire'; x: number; y: number; projectile: ProjectileEventVisual }
  | { type: 'stageComplete'; stage: number }
  | { type: 'eraComplete'; eraIndex: number }
  | { type: 'portalsOpen' }
  | { type: 'portalBenefit'; title: string }
  | { type: 'stageAdvanced'; era: boolean }
  | { type: 'runOver'; victory: boolean }
  | { type: 'sfx'; id: string; x?: number; y?: number; projectile?: ProjectileEventVisual }
  | { type: 'shake'; strength: number };

export interface ProjectileEventVisual {
  color: number;
  visual: number;
  elements: number;
}

export class EventQueue {
  private events: SimEvent[] = [];

  push(e: SimEvent): void {
    this.events.push(e);
  }

  /** Returns and clears pending events. */
  drain(): SimEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  clear(): void {
    this.events.length = 0;
  }
}
